// src/data/assessment-config.js

// =====================================================================
// 1. SCORING DICTIONARY (DEFINITIONS)
// =====================================================================
export const SCORING_METRICS = {
    // --- PERFORMANCE METRICS (0-100 Average) ---
    'focus': { label: 'Focus', type: 'average' },
    'resilience': { label: 'Resilience', type: 'average' },
    'motivation': { label: 'Motivation', type: 'average' },
    'confidence': { label: 'Confidence', type: 'average' },
    'discipline': { label: 'Self Discipline', type: 'average' },
    'awareness': { label: 'Self Awareness', type: 'average' },
    'adaptability': { label: 'Adaptability', type: 'average' },
    'sleep_perf': { label: 'Sleep (Performance)', type: 'average' },
    'nutrition': { label: 'Nutrition & Hydration', type: 'average' },
    'injury_mgmt': { label: 'Pain & Injury Management', type: 'average' },
    
    // --- CLINICAL METRICS (Summation) ---
    'anxiety': { label: 'Anxiety (GAD-7)', type: 'sum' },
    'depression': { label: 'Depression (PHQ-9)', type: 'sum' },
    'sleep_clinical': { label: 'Sleep Disturbance', type: 'sum' }, 
    'alcohol': { label: 'Alcohol Misuse', type: 'sum' },
    'substance': { label: 'Substance Misuse', type: 'sum' },
    'eating': { label: 'Disordered Eating', type: 'sum' },
    'adhd': { label: 'ADHD (ASRS)', type: 'sum' },
    'trauma': { label: 'Trauma (PTSD)', type: 'sum' },
    'gambling': { label: 'Gambling', type: 'sum' },

    // --- YOUTH SCREENER METRICS (Single Question 0-3) ---
    'anxiety_screener': { label: 'Anxiety (Screener)', type: 'sum' },
    'depression_screener': { label: 'Depression (Screener)', type: 'sum' },
    'sleep_screener': { label: 'Sleep (Screener)', type: 'sum' },
    'eating_screener': { label: 'Eating (Screener)', type: 'sum' },
    'adhd_screener': { label: 'ADHD (Screener)', type: 'sum' },
    'trauma_screener': { label: 'Trauma (Screener)', type: 'sum' }
};

// =====================================================================
// 2. REUSABLE CLINICAL MODULES (DETAILED)
// =====================================================================
export const CLINICAL_MODULES = {
  
  // 1. ANXIETY (GAD-7)
  clinical_anxiety: {
    id: "clinical_anxiety",
    title: "Anxiety Screen (GAD-7)",
    description: "Over the last 2 weeks, how often have you been bothered by the following problems?",
    questions: [
      { id: "gad_1", text: "Feeling nervous, anxious, or on edge", type: "scale_0_3", metric: "anxiety" },
      { id: "gad_2", text: "Not being able to stop or control worrying", type: "scale_0_3", metric: "anxiety" },
      { id: "gad_3", text: "Worrying too much about different things", type: "scale_0_3", metric: "anxiety" },
      { id: "gad_4", text: "Trouble relaxing", type: "scale_0_3", metric: "anxiety" },
      { id: "gad_5", text: "Being so restless that it is hard to sit still", type: "scale_0_3", metric: "anxiety" },
      { id: "gad_6", text: "Becoming easily annoyed or irritable", type: "scale_0_3", metric: "anxiety" },
      { id: "gad_7", text: "Feeling afraid, as if something awful might happen", type: "scale_0_3", metric: "anxiety" }
    ]
  },

  // 2. DEPRESSION (PHQ-9)
  clinical_depression: {
    id: "clinical_depression",
    title: "Mood Screen (PHQ-9)",
    description: "Over the last 2 weeks, how often have you been bothered by any of the following problems?",
    questions: [
      { id: "phq_1", text: "Little interest or pleasure in doing things", type: "scale_0_3", metric: "depression" },
      { id: "phq_2", text: "Feeling down, depressed, or hopeless", type: "scale_0_3", metric: "depression" },
      { id: "phq_3", text: "Trouble falling or staying asleep, or sleeping too much", type: "scale_0_3", metric: "depression" },
      { id: "phq_4", text: "Feeling tired or having little energy", type: "scale_0_3", metric: "depression" },
      { id: "phq_5", text: "Poor appetite or overeating", type: "scale_0_3", metric: "depression" },
      { id: "phq_6", text: "Feeling bad about yourself — or that you are a failure", type: "scale_0_3", metric: "depression" },
      { id: "phq_7", text: "Trouble concentrating on things, such as reading or watching TV", type: "scale_0_3", metric: "depression" },
      { id: "phq_8", text: "Moving or speaking so slowly that other people could have noticed", type: "scale_0_3", metric: "depression" },
      { id: "phq_9", text: "Thoughts that you would be better off dead, or of hurting yourself", type: "scale_0_3", metric: "depression" }
    ]
  },

  // 3. SLEEP DISTURBANCE
  clinical_sleep: {
    id: "clinical_sleep",
    title: "Sleep Habits Screen",
    description: "Please rate your typical sleep habits over the recent past.",
    questions: [
      { 
        id: "sleep_1",
        text: "How many hours of ACTUAL sleep did you get at night?",
        type: "select",
        metric: "sleep_clinical",
        options: ["More than 9 hours", "8 to 9 hours", "7 to 8 hours", "6 to 7 hours", "5 to 6 hours", "Less than 5 hours"],
        // Sleep duration is U-SHAPED, not monotonic: hypersomnia (>9h) is a
        // depression-associated symptom, not the best answer — so index-order
        // scoring is wrong for this item. DRAFT values pending clinical
        // sign-off (see docs/archive/ROLLOUT-CHECKLIST.md, Phase 0 / B3 follow-up).
        optionScores: [2, 0, 0, 1, 2, 3]
      },
      { id: "sleep_2", text: "How satisfied/dissatisfied are you with your sleep quality?", type: "scale_0_4", metric: "sleep_clinical" }, 
      { 
        id: "sleep_3", 
        text: "How long has it usually taken you to fall asleep?", 
        type: "select", 
        metric: "sleep_clinical",
        options: ["15 minutes or less", "16 – 30 minutes", "31 – 60 minutes", "Longer than 60 minutes"]
      },
      { id: "sleep_4", text: "How often do you have trouble staying asleep?", type: "scale_0_3", metric: "sleep_clinical" },
      { id: "sleep_5", text: "How often have you taken medicine to help you sleep?", type: "scale_0_3", metric: "sleep_clinical" }
    ]
  },

  // 4. ALCOHOL MISUSE
  clinical_alcohol: {
    id: "clinical_alcohol",
    title: "Alcohol Screen",
    description: "Please select the option that represents your alcohol use.",
    questions: [
      { 
        id: "alc_1", 
        text: "How often do you have a drink containing alcohol?", 
        type: "select", 
        metric: "alcohol",
        options: ["Never", "Monthly or less", "2-4 times a month", "2-3 times a week", "4+ times a week"]
      },
      { 
        id: "alc_2", 
        text: "How many drinks do you have on a typical day when you drink?", 
        type: "select", 
        metric: "alcohol",
        options: ["1 or 2", "3 or 4", "5 or 6", "7 to 9", "10 or more"]
      },
      { 
        id: "alc_3", 
        text: "How often do you have six or more drinks on one occasion?", 
        type: "select", 
        metric: "alcohol",
        options: ["Never", "Less than monthly", "Monthly", "Weekly", "Daily"]
      }
    ]
  },

  // 5. SUBSTANCE MISUSE
  clinical_substance: {
    id: "clinical_substance",
    title: "Substance Screen",
    description: "Regarding drugs/substances (excluding alcohol) in the last 3 months:",
    questions: [
      { id: "drug_1", text: "Have you felt you should cut down or stop using drugs?", type: "yes_no", metric: "substance" },
      { id: "drug_2", text: "Has anyone annoyed you by criticizing your drug use?", type: "yes_no", metric: "substance" },
      { id: "drug_3", text: "Have you felt guilty or bad about how much you use?", type: "yes_no", metric: "substance" },
      { id: "drug_4", text: "Have you woken up wanting to use drugs?", type: "yes_no", metric: "substance" }
    ]
  },

  // 6. EATING
  clinical_eating: {
    id: "clinical_eating",
    title: "Eating Habits Screen",
    description: "Over the past 2 weeks, how often have you been bothered by these problems?",
    questions: [
      { id: "eat_1", text: "I feel extremely guilty after overeating", type: "scale_0_3", metric: "eating" }, 
      { id: "eat_2", text: "I am preoccupied with the desire to be thinner", type: "scale_0_3", metric: "eating" },
      { id: "eat_3", text: "I think that my stomach is too big", type: "scale_0_3", metric: "eating" },
      { id: "eat_4", text: "I feel DISSATISFIED with the shape of my body", type: "scale_0_3", metric: "eating" }, 
      { id: "eat_5", text: "My parents have expected excellence of me", type: "scale_0_3", metric: "eating" },
      { id: "eat_6", text: "As a child, I tried hard to avoid disappointing parents/teachers", type: "scale_0_3", metric: "eating" }
    ]
  },

  // 7. ADHD
  clinical_adhd: {
    id: "clinical_adhd",
    title: "Focus & Attention Screen",
    description: "Over the past 6 months, how often have you...",
    questions: [
      { id: "adhd_1", text: "Had trouble wrapping up final details of a project?", type: "scale_0_4", metric: "adhd" },
      { id: "adhd_2", text: "Had difficulty getting things in order for a task?", type: "scale_0_4", metric: "adhd" },
      { id: "adhd_3", text: "Had problems remembering appointments or obligations?", type: "scale_0_4", metric: "adhd" },
      { id: "adhd_4", text: "Avoided or delayed starting tasks that require thought?", type: "scale_0_4", metric: "adhd" },
      { id: "adhd_5", text: "Fidgeted or squirmed with hands/feet?", type: "scale_0_4", metric: "adhd" },
      { id: "adhd_6", text: "Felt overly active, like you were driven by a motor?", type: "scale_0_4", metric: "adhd" }
    ]
  },

  // 8. TRAUMA
  clinical_trauma: {
    id: "clinical_trauma",
    title: "Trauma Screen",
    description: "In the past month, have you...",
    questions: [
      { id: "ptsd_1", text: "Had nightmares or thought about the event when you didn't want to?", type: "yes_no", metric: "trauma" },
      { id: "ptsd_2", text: "Tried hard not to think about it or avoided situations that reminded you?", type: "yes_no", metric: "trauma" },
      { id: "ptsd_3", text: "Been constantly on guard, watchful, or easily startled?", type: "yes_no", metric: "trauma" },
      { id: "ptsd_4", text: "Felt numb or detached from people/activities?", type: "yes_no", metric: "trauma" },
      { id: "ptsd_5", text: "Felt guilty or unable to stop blaming yourself?", type: "yes_no", metric: "trauma" }
    ]
  },

  // 9. GAMBLING
  clinical_gambling: {
    id: "clinical_gambling",
    title: "Gambling Screen",
    description: "In the last 12 months...",
    questions: [
      { id: "gam_1", text: "Have you bet more than you could really afford to lose?", type: "scale_0_3", metric: "gambling" },
      { id: "gam_2", text: "Needed to gamble with larger amounts to get excitement?", type: "scale_0_3", metric: "gambling" },
      { id: "gam_3", text: "Gone back another day to win back money you lost?", type: "scale_0_3", metric: "gambling" },
      { id: "gam_4", text: "Borrowed money or sold things to gamble?", type: "scale_0_3", metric: "gambling" },
      { id: "gam_5", text: "Felt you might have a problem with gambling?", type: "scale_0_3", metric: "gambling" },
      { id: "gam_6", text: "Has gambling caused you health problems (stress/anxiety)?", type: "scale_0_3", metric: "gambling" },
      { id: "gam_7", text: "Have people criticized your betting?", type: "scale_0_3", metric: "gambling" },
      { id: "gam_8", text: "Has gambling caused financial problems?", type: "scale_0_3", metric: "gambling" },
      { id: "gam_9", text: "Felt guilty about the way you gamble?", type: "scale_0_3", metric: "gambling" }
    ]
  }
};

// =====================================================================
// 3. ASSESSMENT LIBRARY (DEFAULT CONFIGS)
// =====================================================================

export const ASSESSMENT_LIBRARY = {
  
  // === ADULT STREAM (16+) ===
  "core_v1": {
    title: "Performance & Wellness Baseline",
    audience: "Adult", 
    
    screener: [
      // STEP 1: PRE-SCREEN
      {
        id: "section_prescreen",
        title: "Context & Health",
        description: "Let's establish your baseline context.",
        questions: [
           { id: "pre_health", text: "How would you rate your current Mental Health?", type: "slider_100" },
           { id: "pre_injury", text: "How are you feeling physically right now?", type: "slider_100" },
           { id: "pre_coaching", text: "How do you prefer to receive feedback?", type: "select", options: ["Direct & Blunt", "Supportive & Encouraging", "Analytical & Data-Driven", "Visual Examples"] }
        ]
      },

      // STEP 2: PERFORMANCE METRICS
      {
        id: "section_performance",
        title: "Performance Metrics",
        description: "Rate your current state (0-100).",
        questions: [
          { id: "mp_focus", text: "I can maintain focus on my tasks despite distractions.", type: "slider_100", metric: "focus" },
          { id: "mp_resilience", text: "I recover quickly from setbacks or failures.", type: "slider_100", metric: "resilience" },
          { id: "mp_motivation", text: "I feel motivated to achieve my daily goals.", type: "slider_100", metric: "motivation" },
          { id: "mp_confidence", text: "I believe in my ability to succeed.", type: "slider_100", metric: "confidence" },
          { id: "mp_discipline", text: "I stick to my training and routines even when I don't feel like it.", type: "slider_100", metric: "discipline" },
          { id: "mp_awareness", text: "I am aware of my emotional state and how it affects my performance.", type: "slider_100", metric: "awareness" },
          { id: "mp_adaptability", text: "I can easily adjust my plans when things go wrong.", type: "slider_100", metric: "adaptability" },
          { id: "mp_sleep", text: "I feel rested and recovered after a night's sleep.", type: "slider_100", metric: "sleep_perf" },
          { id: "mp_nutrition", text: "I fuel my body consistently for high performance.", type: "slider_100", metric: "nutrition" },
          { id: "mp_pain", text: "I manage physical pain effectively without letting it derail me.", type: "slider_100", metric: "injury_mgmt" }
        ]
      },

      // STEP 3: SELF ASSESSMENT
      {
        id: "section_self_sort",
        title: "Self Perception",
        description: "Rank these areas from strongest (top) to weakest (bottom).",
        questions: [
            { 
                id: "self_rank_skills", 
                text: "Drag to rank your perceived strengths:", 
                type: "drag_sort", 
                options: ["Mental Toughness", "Technical Skill", "Physical Fitness", "Emotional Control", "Leadership", "Rest & Recovery"] 
            }
        ]
      },

      // STEP 4: MENTAL HEALTH SCREEN (GATES)
      {
        id: "section_mental_health",
        title: "Wellness Check",
        description: "Please answer honestly. These questions determine if we need to dig deeper.",
        questions: [
          // ANXIETY
          {
            id: "mh_anxiety_screen",
            text: "In the last 2 weeks, how often have you felt nervous, anxious, or on edge?",
            type: "scale_0_3", 
            trigger: { threshold: 2, module: "clinical_anxiety" },
            metric: "anxiety" 
          },
          // DEPRESSION
          {
            id: "mh_depression_screen",
            text: "In the last 2 weeks, how often have you felt down, depressed, or hopeless?",
            type: "scale_0_3",
            trigger: { threshold: 2, module: "clinical_depression" },
            metric: "depression"
          },
          // SLEEP (Clinical)
          {
            id: "mh_sleep_screen",
            text: "How often do you have trouble falling asleep?",
            type: "scale_0_3",
            trigger: { threshold: 2, module: "clinical_sleep" },
            metric: "sleep_clinical" 
          },
          // EATING
          {
            id: "mh_food_screen",
            text: "Do you ever feel like your relationship with food is a source of stress?",
            type: "yes_no", 
            trigger: { threshold: 1, module: "clinical_eating" }, 
            metric: "eating"
          },
          // ALCOHOL
          {
            id: "mh_alcohol_screen",
            text: "How often do you have a drink containing alcohol?",
            type: "select",
            options: ["Never", "Monthly or less", "2-4 times a month", "2-3 times a week", "4+ times a week"],
            trigger: { threshold: 2, module: "clinical_alcohol" }, 
            metric: "alcohol"
          },
          // SUBSTANCE
          {
            id: "mh_substance_screen",
            text: "Have you used drugs or medications (not prescribed) to cope with stress recently?",
            type: "yes_no",
            trigger: { threshold: 1, module: "clinical_substance" },
            metric: "substance"
          },
          // ADHD
          {
            id: "mh_adhd_screen",
            text: "How often do you have trouble wrapping up the final details of a project?",
            type: "scale_0_4",
            trigger: { threshold: 2, module: "clinical_adhd" }, 
            metric: "adhd"
          },
          // TRAUMA
          {
            id: "mh_trauma_screen",
            text: "Have you experienced a frightening or traumatic event that still bothers you?",
            type: "yes_no",
            trigger: { threshold: 1, module: "clinical_trauma" },
            metric: "trauma"
          },
          // GAMBLING
          {
            id: "mh_gambling_screen",
            text: "Have you bet more than you could really afford to lose?",
            type: "scale_0_3",
            trigger: { threshold: 1, module: "clinical_gambling" },
            metric: "gambling"
          }
        ]
      }
    ],
    modules: CLINICAL_MODULES
  },

  // === YOUTH STREAM (Under 15) ===
  "youth_v1": {
    title: "Youth Athlete Check-In",
    audience: "Youth",
    
    screener: [
      // STEP 1: PRE-SCREEN (Simplified)
      {
        id: "section_prescreen",
        title: "Health Check",
        description: "Let's get started.",
        questions: [
           { id: "pre_health", text: "How would you rate your current Mental Health?", type: "slider_100" },
           { id: "pre_injury", text: "How are you feeling physically right now?", type: "slider_100" },
           { id: "pre_coaching", text: "How do you like coaches to talk to you?", type: "select", options: ["Tell me straight", "Cheer me on", "Show me pictures/video", "Explain the 'Why'"] }
        ]
      },

      // STEP 2: PERFORMANCE METRICS (Kid Friendly Wording)
      {
        id: "section_performance",
        title: "My Game",
        description: "Move the slider to show how you feel (0-100).",
        questions: [
          { id: "mp_focus", text: "I can pay attention to my coaches and schoolwork easily.", type: "slider_100", metric: "focus" },
          { id: "mp_resilience", text: "When I make a mistake, I can get back in the game quickly.", type: "slider_100", metric: "resilience" },
          { id: "mp_motivation", text: "I feel excited to try my best every day.", type: "slider_100", metric: "motivation" },
          { id: "mp_confidence", text: "I believe in myself and my skills.", type: "slider_100", metric: "confidence" },
          { id: "mp_discipline", text: "I do my chores and practice even when I don't want to.", type: "slider_100", metric: "discipline" },
          { id: "mp_awareness", text: "I understand why I feel happy, mad, or sad.", type: "slider_100", metric: "awareness" },
          { id: "mp_adaptability", text: "I am okay when plans change suddenly.", type: "slider_100", metric: "adaptability" },
          { id: "mp_sleep", text: "I wake up feeling ready to go.", type: "slider_100", metric: "sleep_perf" },
          { id: "mp_nutrition", text: "I know what foods I need to eat to perform at my best.", type: "slider_100", metric: "nutrition" },
          { id: "mp_pain", text: "I listen to my body when it hurts.", type: "slider_100", metric: "injury_mgmt" }
        ]
      },

      // STEP 3: SELF ASSESSMENT
      {
        id: "section_self_sort",
        title: "My Strengths",
        description: "Rank these from your Best (top) to your Worst (bottom).",
        questions: [
            { 
                id: "self_rank_skills", 
                text: "Drag to rank your skills:", 
                type: "drag_sort", 
                options: ["Trying Hard", "Sports Skills", "Fitness", "Being Calm", "Helping Teammates", "Resting"] 
            }
        ]
      },

      // STEP 4: WELLNESS CHECK (Updated for Youth Safety)
      // * NO TRIGGERS: Ensures youth assessment remains linear (no deep dives).
      // * SCREENER METRICS: Uses unique keys (e.g. 'anxiety_screener') so scores aren't confused with adult GAD-7 totals.
      {
        id: "section_mental_health",
        title: "Feelings Check",
        description: "Be honest. This helps us help you.",
        questions: [
          // ANXIETY
          {
            id: "mh_anxiety_screen",
            text: "In the last 2 weeks, how often have you felt super worried or nervous?",
            type: "scale_0_3", 
            metric: "anxiety_screener" 
          },
          // DEPRESSION
          {
            id: "mh_depression_screen",
            text: "In the last 2 weeks, how often have you felt really sad or like you don't want to play?",
            type: "scale_0_3",
            metric: "depression_screener" 
          },
          // SLEEP (Clinical)
          {
            id: "mh_sleep_screen",
            text: "Do you ever have trouble falling asleep at night?",
            type: "scale_0_3",
            metric: "sleep_screener" 
          },
          // EATING
          {
            id: "mh_food_screen",
            text: "Does eating food ever make you feel stressed or guilty?",
            type: "yes_no", 
            metric: "eating_screener" 
          },
          // ADHD
          {
            id: "mh_adhd_screen",
            text: "Do you have trouble finishing your homework or chores?",
            type: "scale_0_4",
            metric: "adhd_screener" 
          },
          // TRAUMA
          {
            id: "mh_trauma_screen",
            text: "Has something scary ever happened that keeps you from falling asleep?",
            type: "yes_no",
            metric: "trauma_screener" 
          }
        ]
      }
    ],
    modules: CLINICAL_MODULES 
  },
  
// === PARENT CHECK-IN (youth sport) ===
  // A parent's monthly read on THEIR PLAYER, not on themselves. Every metric key
  // is prefixed parent_ so nothing here can be mistaken for the player's own
  // scores: the performance average, the coach aggregates and the warehouse
  // performance rows all ignore these keys (shared/scoringCore.js PARENT_METRICS
  // lists them for the warehouse). Results route the parent to the talk guides
  // (data/talkGuides.js) on the Parent Hub. No radar, no reaction game, no chat.
  "parent_v1": {
    title: "Parent Check-In",
    audience: "Parent",
    configOptions: { includeCognitive: false, includeChatAgent: false, includeRadar: false },
    screener: [
      {
        id: "parent_about_player",
        title: "About Your Player",
        description: "Think about the last month. Move the slider to show how true each statement is.",
        questions: [
          { id: "pq_sleep",      text: "My player wakes up ready to go on most days.",                    type: "slider_100", metric: "parent_sleep",      rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_energy",     text: "My player has the energy to get through school and {sport}.",       type: "slider_100", metric: "parent_energy",     rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_fuel",       text: "My player eats and drinks enough on {session} and {contest} days.",      type: "slider_100", metric: "parent_fuel",       rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_enjoyment",  text: "My player seems to enjoy {sport} right now.",                        type: "slider_100", metric: "parent_enjoyment",  rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] },
          { id: "pq_bounce",     text: "After a bad {contest} or {session}, my player bounces back within a day.", type: "slider_100", metric: "parent_bounce",     rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_confidence", text: "My player believes in themselves on {surface}.",                      type: "slider_100", metric: "parent_confidence", rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] },
          { id: "pq_belonging",  text: "My player seems to feel part of their team.",                       type: "slider_100", metric: "parent_belonging",  rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] },
          { id: "pq_pain",       text: "My player tells me when something hurts.",                          type: "slider_100", metric: "parent_pain",       rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_talks",      text: "My player talks to me when something is wrong.",                    type: "slider_100", metric: "parent_talks",      rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_mood",       text: "My player has seemed like themselves lately.",                      type: "slider_100", metric: "parent_mood",       rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] }
        ]
      },
      {
        id: "parent_at_home",
        title: "At Home",
        description: "A few about the two of you.",
        questions: [
          { id: "pq_car_ride",   text: "The car ride home after a tough {contest} goes okay.",                   type: "slider_100", metric: "parent_car_ride",   rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_balance",    text: "{Sport} stays in a healthy place in our home life.",                  type: "slider_100", metric: "parent_balance",    rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] },
          { id: "pq_words",      text: "I know what to say when my player is upset about {sport}.",          type: "slider_100", metric: "parent_words",      rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] }
        ]
      }
    ],
    modules: {}
  },

// === GUARDIAN CHECK-IN (school) ===
  // parent_v1 for a school division: the same parent_* keys and the same routing
  // to the talk guides, with the sentences about school days instead of practices
  // and games. Picked by assessmentByMemberType on a school org (sport 'school').
  "parent_school_v1": {
    title: "Guardian Check-In",
    audience: "Parent",
    configOptions: { includeCognitive: false, includeChatAgent: false, includeRadar: false },
    screener: [
      {
        id: "parent_about_player",
        title: "About Your Student",
        description: "Think about the last month. Move the slider to show how true each statement is.",
        questions: [
          { id: "pq_sleep",      text: "My student wakes up ready for the day on most days.",                       type: "slider_100", metric: "parent_sleep",      rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_energy",     text: "My student has the energy to get through the school day and what comes after it.", type: "slider_100", metric: "parent_energy", rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_fuel",       text: "My student eats and drinks enough on school days.",                        type: "slider_100", metric: "parent_fuel",       rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_enjoyment",  text: "My student seems to enjoy school right now.",                              type: "slider_100", metric: "parent_enjoyment",  rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] },
          { id: "pq_bounce",     text: "After a hard day or a bad test, my student bounces back within a day.",    type: "slider_100", metric: "parent_bounce",     rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_confidence", text: "My student believes in themselves in class.",                             type: "slider_100", metric: "parent_confidence", rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] },
          { id: "pq_belonging",  text: "My student seems to feel part of their class and their friend group.",    type: "slider_100", metric: "parent_belonging",  rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] },
          { id: "pq_pain",       text: "My student tells me when something hurts.",                               type: "slider_100", metric: "parent_pain",       rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_talks",      text: "My student talks to me when something is wrong.",                         type: "slider_100", metric: "parent_talks",      rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_mood",       text: "My student has seemed like themselves lately.",                           type: "slider_100", metric: "parent_mood",       rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] }
        ]
      },
      {
        id: "parent_at_home",
        title: "At Home",
        description: "A few about the two of you.",
        questions: [
          { id: "pq_car_ride",   text: "The ride home after a hard day goes okay.",                                type: "slider_100", metric: "parent_car_ride",   rangeLabels: ['Rarely', 'Sometimes', 'Usually', 'Almost Always'] },
          { id: "pq_balance",    text: "School stays in a healthy place in our home life.",                        type: "slider_100", metric: "parent_balance",    rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] },
          { id: "pq_words",      text: "I know what to say when my student is upset about school.",               type: "slider_100", metric: "parent_words",      rangeLabels: ['Not Really', 'A Little', 'Mostly', 'Very Much'] }
        ]
      }
    ],
    modules: {}
  },

// === MICRO-PULSE STREAM (youth) ===
  // The weekly pulse a player under 18 gets (useAssessmentLogic picks it by age
  // tier). Same three metrics as the adult pulse so the trend and the coach
  // aggregates line up; only the words and the slider labels differ.
  "micro_pulse_youth_v1": {
    title: "Weekly Check-In",
    audience: "Youth",
    configOptions: { includeCognitive: false, includeChatAgent: false },
    screener: [
      {
        id: "section_pulse",
        title: "Weekly Check-In",
        description: "Three quick questions. About 30 seconds.",
        questions: [
          { id: "pulse_sleep", text: "How rested do you feel today?", type: "slider_100", metric: "sleep_perf", rangeLabels: ['Not Rested', 'A Little', 'Pretty Rested', 'Fully Charged'] },
          { id: "pulse_stress", text: "How much is on your plate right now, with school, sport and everything else?", type: "slider_100", metric: "workload", rangeLabels: ['Not Much', 'Some', 'A Lot', 'Too Much'] },
          // Overwritten with the player's lowest area each week; the labels stay.
          { id: "pulse_dynamic", text: "How did this week go?", type: "slider_100", metric: "focus", rangeLabels: ['Rough', 'Okay', 'Good', 'Great'] }
        ]
      }
    ]
  },

// === MICRO-PULSE STREAM ===
  "micro_pulse_v1": {
    title: "Weekly Performance Pulse",
    audience: "Universal",
    configOptions: { includeCognitive: false },
    screener: [
      {
        id: "section_pulse",
        title: "Weekly Check-In",
        description: "3 quick questions to calibrate your AI forecast.",
        questions: [
          { id: "pulse_sleep", text: "How recovered and rested do you feel today?", type: "slider_100", metric: "sleep_perf" },
          // Its OWN key: a 0-100 slider must never be summed into the GAD-7 ('anxiety', /21).
          { id: "pulse_stress", text: "How heavy is your mental workload right now?", type: "slider_100", metric: "workload" },
          // The 3rd question is a placeholder that our hook will overwrite dynamically
          { id: "pulse_dynamic", text: "How is your focus this week?", type: "slider_100", metric: "focus" }
        ]
      }
    ]
  }
};